import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { KPI, Panel, EmptyRow } from "@/components/DashboardWidgets";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Users, Calendar, ClipboardCheck, UserX, BookOpen, ClipboardList, Bell, FileText, Wallet, Printer } from "lucide-react";
import { toast } from "sonner";

const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;

export default function TeacherDashboard() {
  const [d, setD] = useState({});
  const [slipMonth, setSlipMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payroll, setPayroll] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [notices, setNotices] = useState([]);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    api.get("/stats/teacher").then((r) => setD(r.data)).catch(() => {});
    api.get("/erp/payroll").then((r) => setPayroll(r.data)).catch(() => {});
    api.get("/erp/payroll/advances").then((r) => setAdvances(r.data)).catch(() => {});
    api.get("/erp/notices").then((r) => setNotices(r.data)).catch(() => {});
    api.get("/erp/holidays?upcoming=true").then((r) => setHolidays(r.data)).catch(() => {});
  }, []);

  const monthRow = payroll.find((p) => p.month === slipMonth);
  const monthAdvances = advances.filter((a) => (a.date || "").startsWith(slipMonth));
  const monthAdvTotal = monthAdvances.reduce((n, a) => n + (a.amount || 0), 0);
  const netAfterAdvances = (monthRow?.net || 0) - monthAdvTotal;

  const printSlip = () => {
    if (!monthRow) { toast.error(`No payslip for ${slipMonth}`); return; }
    const w = window.open("", "_blank"); if (!w) return;
    const advRows = monthAdvances.map((a) => `<div class="row"><b>Advance · ${a.date}</b><span class="neg">-${inr(a.amount)}</span></div>`).join("");
    w.document.write(`<html><head><title>Payslip ${monthRow.employee_name} ${monthRow.month}</title>
      <style>body{font-family:system-ui;padding:24px;max-width:640px;margin:auto;color:#0f172a}
      h1{margin:0;font-size:20px} .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e2e8f0}
      .box{border:2px solid #0f172a;padding:16px;margin-top:16px} .total{font-weight:800;font-size:16px}
      .neg{color:#b91c1c;font-weight:700} .pos{color:#059669;font-weight:700}</style>
      </head><body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div><h1>Payslip</h1><div>Month: ${monthRow.month}</div></div>
        <div style="text-align:right"><div class="total">${monthRow.status === "paid" ? "PAID" : "PENDING"}</div>${monthRow.paid_on ? `<div>${monthRow.paid_on}</div>` : ""}</div>
      </div>
      <div class="box">
        <div class="row"><b>Employee</b><span>${monthRow.employee_name}</span></div>
        <div class="row"><b>Role</b><span>${monthRow.role}</span></div>
        <div class="row"><b>Basic</b><span>${inr(monthRow.basic)}</span></div>
        <div class="row"><b>Allowances</b><span class="pos">+${inr(monthRow.allowances)}</span></div>
        <div class="row"><b>Deductions</b><span class="neg">-${inr(monthRow.deductions)}</span></div>
        ${advRows}
        <div class="row total"><b>Net Payable (after advances)</b><span>${inr(netAfterAdvances)}</span></div>
      </div>
      <p style="margin-top:24px;text-align:right;color:#64748b">Authorized Signatory</p>
      <script>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <DashLayout variant="teacher" title="Teacher Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Users} label="My Classes" value={(d.my_classes || []).length} color="text-blue-700 bg-blue-50" testId="tk-classes" />
        <KPI icon={Calendar} label="Today's Sessions" value={(d.today_sessions || []).length} color="text-emerald-700 bg-emerald-50" testId="tk-sessions" />
        <KPI icon={ClipboardCheck} label="Attendance Pending" value={d.attendance_pending} color="text-amber-700 bg-amber-50" testId="tk-att-pending" />
        <KPI icon={UserX} label="Students Absent" value={d.students_absent} color="text-red-700 bg-red-50" testId="tk-absent" />
        <KPI icon={BookOpen} label="Upcoming Exams" value={(d.upcoming_exams || []).length} color="text-indigo-700 bg-indigo-50" testId="tk-exams" />
        <KPI icon={ClipboardList} label="Assignments" value="—" sub="soon" color="text-purple-700 bg-purple-50" testId="tk-asg" />
        <KPI icon={Bell} label="Notices" value={(notices || []).length} color="text-pink-700 bg-pink-50" testId="tk-notice" />
        <KPI icon={FileText} label="Leave Requests" value={(d.my_leaves || []).length} color="text-slate-700 bg-slate-100" testId="tk-leave" />
      </div>

      <Card className="p-5 mt-4 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-white" data-testid="salary-slip-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded bg-emerald-100 flex items-center justify-center"><Wallet className="w-5 h-5 text-emerald-700" /></div>
          <div>
            <h3 className="font-bold text-slate-900">My Salary Slip</h3>
            <p className="text-xs text-slate-500">Pick any month — advances are auto-deducted from Net Payable</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Month</Label>
            <Input type="month" value={slipMonth} onChange={(e) => setSlipMonth(e.target.value)} className="w-40" data-testid="slip-month" />
          </div>
          {monthRow ? (
            <>
              <div className="text-sm">
                <div className="text-[10px] uppercase text-slate-500">Basic + Allow. − Deduct.</div>
                <div className="font-bold tabular-nums">{inr(monthRow.net)}</div>
              </div>
              {monthAdvTotal > 0 && (
                <div className="text-sm">
                  <div className="text-[10px] uppercase text-amber-700">Advances this month</div>
                  <div className="font-bold tabular-nums text-amber-700">−{inr(monthAdvTotal)}</div>
                </div>
              )}
              <div className="text-sm">
                <div className="text-[10px] uppercase text-emerald-700">Net Payable</div>
                <div className="text-xl font-black tabular-nums text-emerald-700" data-testid="slip-net">{inr(netAfterAdvances)}</div>
              </div>
              <Button onClick={printSlip} className="bg-emerald-700 hover:bg-emerald-800" data-testid="slip-download"><Printer className="w-4 h-4 mr-1" />Download PDF</Button>
            </>
          ) : (
            <div className="text-sm text-slate-500">No payslip for {slipMonth} yet — HR will generate it soon.</div>
          )}
        </div>
        {monthAdvances.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {monthAdvances.map((a) => (
              <Badge key={a.id} variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">
                {a.date} · {inr(a.amount)} · {a.reason || "advance"}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Panel title="My Classes" testId="tk-my-classes">
          {(d.my_classes || []).length === 0 ? <EmptyRow text="You are not assigned to any class yet" /> : (
            <div className="flex flex-wrap gap-2">
              {d.my_classes.map((c) => <Badge key={c} variant="outline" className="border-blue-200 text-blue-700">Class {c}</Badge>)}
            </div>
          )}
        </Panel>
        <Panel title="Today's Timetable" testId="tk-timetable" action={<Link to="/school/timetable" className="text-xs text-blue-700 hover:underline">View all →</Link>}>
          {(d.today_sessions || []).length === 0 ? <EmptyRow text="No sessions today" /> : (
            <ul className="space-y-2">
              {d.today_sessions.map((t, i) => (
                <li key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><div className="font-semibold">Class {t.class_name} · {t.subject}</div><div className="text-xs text-slate-500">Period {t.period || "—"}</div></div>
                  <div className="text-xs text-slate-500 font-mono">{t.day}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Upcoming Exams" testId="tk-upcoming-exams">
          {(d.upcoming_exams || []).length === 0 ? <EmptyRow text="No upcoming exams" /> : (
            <ul className="space-y-2">
              {d.upcoming_exams.map((e) => (
                <li key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><div className="font-semibold">{e.name}</div><div className="text-xs text-slate-500">Class {e.class_name} · {e.subject}</div></div>
                  <div className="text-xs text-slate-500 font-mono">{e.exam_date}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Recent Notices" testId="tk-notices" action={<Link to="/erp/notices" className="text-xs text-blue-700 hover:underline">All →</Link>}>
          {(notices || []).length === 0 ? <EmptyRow text="No notices" /> : (
            <ul className="space-y-2">
              {notices.slice(0, 5).map((n) => (
                <li key={n.id} className="text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`tk-notice-${n.id}`}>
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-xs text-slate-500">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 10)} · {n.audience}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="My Leave Requests" testId="tk-leaves" action={<Link to="/school/leaves" className="text-xs text-blue-700 hover:underline">Apply →</Link>}>
          {(d.my_leaves || []).length === 0 ? <EmptyRow text="No leave requests" /> : (
            <ul className="space-y-2">
              {d.my_leaves.slice(0, 5).map((lv, i) => (
                <li key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div>{lv.from_date} → {lv.to_date}</div>
                  <Badge variant="outline">{lv.status || "pending"}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Upcoming Holidays" testId="tk-holidays">
          {holidays.length === 0 ? <EmptyRow text="No upcoming holidays" /> : (
            <ul className="space-y-2">
              {holidays.slice(0, 6).map((h) => (
                <li key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`tk-holiday-${h.id}`}>
                  <div className="font-semibold">{h.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{h.date} · <Badge variant="outline">{h.scope}</Badge></div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </DashLayout>
  );
}
